import React from 'react';
import PropTypes from 'prop-types';
import Link from '@components/shared/Link';
import cx from 'classnames';
import { useAppContext } from '@/context/AppContext';
import { useMasonryColumns } from '@/masonry';
import ImageCycler from './ImageCycler';
import { setScaledDragImage } from './dragImage';
import ImageTileActionButtons from '@components/preview/masonry/ImageTileActionButtons';
import { getCroppedAspectRatio, normalizeCrop } from '@components/shared/CroppedImage';
import { getPagePath } from '@/utils/pagePaths';
import { flattenImages } from '@components/shared/ImageViewer/imageViewerUtils';
import { prefetchLowFiles } from '@components/shared/ImageViewer/picture';
import { useDragEdgeScroll } from './dragEdgeScroll';
import { galleryGapCss } from '@/blocks/spacing';
import { galleryDeviceLayout } from '@/blocks/gallery-devices';
import useTextDevice from '@/hooks/useTextDevice';

/**
 * DirectoryMasonry Component
 * 
 * Displays a masonry grid of linked pages, where each tile cycles through
 * multiple images and links to another page in the site.
 * 
 * Reuses the existing masonry layout logic from useMasonryColumns hook.
 */
function placeBefore(ids, fromId, toId) {
    if (!fromId || fromId === toId) return ids;
    const next = ids.filter((id) => id !== fromId);
    const at = next.indexOf(toId);
    next.splice(at < 0 ? next.length : at, 0, fromId);
    return next;
}

function DirectoryMasonry({ 
    links = [], 
    hide = false, 
    columns: baseColumns, 
    breakpoints: customBreakpoints, 
    gap: baseGap = 4,
    devices = null,
    // Instant swap by default, matching how every existing directory behaves.
    crossfade = false,
    arrange = false,
    onArrange,
    onTileEdit,
}) {
    const { content } = useAppContext();
    const containerRef = React.useRef(null);
    
    // Get all pages and files data
    const pages = content?.pages || {};
    const files = content?.files || {};
    
    // Prepare link items with resolved data
    const linkItems = React.useMemo(() => {
        if (!links || !Array.isArray(links)) return [];
        
        return links
            .map((link, index) => {
                const linkedPage = pages[link.pageId];
                if (!linkedPage) return null;
                
                // Get first image for display (determines aspect ratio)
                const firstImageId = link.imageIds?.[0];
                const firstImage = firstImageId ? files[firstImageId] : null;
                
                // Calculate aspect ratio based on first image crop or image dimensions
                // This is a numeric ratio (height/width) used for masonry column distribution
                let aspectRatio = 1;
                if (firstImage?.dimensions) {
                    const crop = normalizeCrop(link.crops?.[firstImageId]);
                    if (crop) {
                        const croppedWidth = firstImage.dimensions.width * crop.width;
                        const croppedHeight = firstImage.dimensions.height * crop.height;
                        aspectRatio = croppedHeight / croppedWidth;
                    } else {
                        aspectRatio = firstImage.dimensions.height / firstImage.dimensions.width;
                    }
                }
                
                // Calculate full page path for nested pages
                const fullPagePath = getPagePath(linkedPage, pages);
                const pageUrl = fullPagePath ? `/${fullPagePath}` : '#';
                
                return {
                    ...link,
                    linkedPage,
                    firstImage,
                    aspectRatio,
                    originalIndex: index,
                    pageUrl,
                };
            })
            .filter(Boolean);
    }, [links, pages, files]);

    // Grid-sized copies of every picture in the linked stories. Opening one
    // then only has to fetch the full-size files, and that pauses this queue.
    React.useEffect(() => {
        const list = [];
        const seen = new Set();
        linkItems.forEach((item) => {
            const ids = flattenImages(item.linkedPage?.gallery?.images || []);
            (item.imageIds || []).forEach((id) => ids.push(id));
            ids.forEach((id) => {
                if (!id || seen.has(id) || !files[id]) return;
                seen.add(id);
                list.push(files[id]);
            });
        });
        prefetchLowFiles(list);
    }, [linkItems, files]);
    
    // Height estimation for masonry distribution
    const getItemHeight = React.useCallback((item) => {
        return item.aspectRatio || 1;
    }, []);
    
    const device = useTextDevice();
    const layout = galleryDeviceLayout({
        columns: baseColumns,
        breakpoints: customBreakpoints,
        gap: baseGap,
        devices,
    })[device === 'desktop' ? 'base' : device];
    const gap = layout.gap;

    // Distribute links into columns
    const { columns, columnCount } = useMasonryColumns({
        items: linkItems,
        containerRef,
        getItemHeight,
        columnCount: layout.columns,
    });
    
    const [overId, setOverId] = React.useState(null);
    const dragRef = React.useRef(null);
    const orderIds = linkItems.map((item) => item.pageId);
    useDragEdgeScroll(arrange, containerRef);

    const dropOn = (targetId) => {
        const fromId = dragRef.current;
        dragRef.current = null;
        setOverId(null);
        if (!arrange || !onArrange || !fromId) return;
        const next = placeBefore(orderIds, fromId, targetId);
        if (next.join('\0') !== orderIds.join('\0')) onArrange(next);
    };

    if (!linkItems.length) return null;

    const gapCss = galleryGapCss(gap, columnCount);
    
    return (
        <section 
            ref={containerRef}
            className={cx(
                'directory-masonry-container page-column flex transition-opacity duration-200',
                { 'opacity-0': hide }
            )}
            style={{
                gap: gapCss,
                paddingLeft: gapCss,
                paddingRight: gapCss,
            }}
            role="feed"
            aria-label="Directory gallery"
            data-page-path="page.grid"
        >
            {columns.map((column, colIndex) => (
                <div 
                    key={colIndex} 
                    className="directory-masonry-column flex-1 flex flex-col min-w-0"
                    style={{ gap: gapCss }}
                >
                    {column.map(({ item }) => (
                        <DirectoryTile
                            key={item.pageId}
                            link={item}
                            files={files}
                            tabIndex={item.originalIndex + 1}
                            crossfade={crossfade}
                            arrange={arrange}
                            onTileEdit={onTileEdit}
                            over={overId === item.pageId}
                            onDragStart={() => { dragRef.current = item.pageId; }}
                            onDragOver={() => setOverId(item.pageId)}
                            onDrop={() => dropOn(item.pageId)}
                            onDragEnd={() => { dragRef.current = null; setOverId(null); }}
                        />
                    ))}
                </div>
            ))}
        </section>
    );
}

DirectoryMasonry.propTypes = {
    /** When true, fade between a tile's images instead of swapping instantly */
    crossfade: PropTypes.bool,
    /** Array of link objects from gallery.links */
    links: PropTypes.arrayOf(PropTypes.shape({
        pageId: PropTypes.string.isRequired,
        description: PropTypes.string,
        imageIds: PropTypes.arrayOf(PropTypes.string),
        cropRatio: PropTypes.string,
        cycleDuration: PropTypes.number,
        crops: PropTypes.object,
    })),
    /** Hide the gallery (for transitions) */
    hide: PropTypes.bool,
    /** Base column count from database (optional, defaults to 5) */
    columns: PropTypes.number,
    /** Older width breakpoints; they now only narrow tablet and phone columns */
    breakpoints: PropTypes.object,
    /** Gap: a 0–20 number (shared vw) or { value, unit } */
    gap: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    /** Tablet and phone columns and gap that differ from desktop's. */
    devices: PropTypes.object,
    /** While a directory block is open in the preview, tiles can be dragged into a new order. */
    arrange: PropTypes.bool,
    onArrange: PropTypes.func,
    onTileEdit: PropTypes.func,
};

/**
 * Individual directory tile with image cycling and page link
 */
function DirectoryTile({ link, files, tabIndex, crossfade = false, arrange = false, over = false, onDragStart, onDragOver, onDrop, onDragEnd, onTileEdit }) {
    const { 
        pageId, 
        description, 
        imageIds = [], 
        cycleDuration = 3000, 
        crops = {},
        linkedPage,
        firstImage,
        pageUrl,
    } = link;
    
    // Build image data object for ImageCycler
    const imageData = React.useMemo(() => {
        const data = {};
        for (const imageId of imageIds) {
            const file = files[imageId];
            if (file) {
                data[imageId] = file;
            }
        }
        return data;
    }, [imageIds, files]);
    
    // Calculate aspect ratio from first image + crop (CSS string format)
    const aspectRatio = React.useMemo(() => {
        const crop = crops[imageIds[0]];
        return getCroppedAspectRatio(firstImage?.dimensions, crop);
    }, [firstImage, crops, imageIds]);
    
    // Display name - use description if provided, otherwise page name
    const displayName = description || linkedPage?.name || 'Untitled';
    
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
        <figure 
            className={cx('directory-tile relative m-0', arrange && 'cursor-grab [&_img]:pointer-events-none', over && 'ring-2 ring-purple-500')}
            data-page-id={pageId}
            data-scroll-to={pageId}
            data-page-path="page.link"
            draggable={arrange || undefined}
            onDragStart={(event) => {
                if (!arrange) return;
                if (event.target.closest?.('[data-action-button], input, textarea, button')) {
                    event.preventDefault();
                    return;
                }
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', pageId);
                setScaledDragImage(event, event.currentTarget);
                onDragStart?.();
            }}
            onDragOver={(event) => {
                if (!arrange) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                onDragOver?.();
            }}
            onDrop={(event) => {
                if (!arrange) return;
                event.preventDefault();
                onDrop?.();
            }}
            onDragEnd={() => onDragEnd?.()}
        >
            <Link 
                to={pageUrl}
                draggable={false}
                className="block group relative focus:outline-none"
                aria-label={displayName}
                tabIndex={tabIndex !== undefined ? tabIndex : 0}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={arrange ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                } : undefined}
            >
                <div className="tile-container w-full relative overflow-hidden">
                    {imageIds.length > 0 ? (
                        <ImageCycler
                            imageIds={imageIds}
                            cycleDuration={cycleDuration}
                            crops={crops}
                            imageData={imageData}
                            aspectRatio={aspectRatio}
                            crossfade={crossfade}
                        />
                    ) : (
                        <div 
                            className="w-full"
                            style={{ 
                                aspectRatio: '1 / 1',
                                backgroundColor: 'color-mix(in srgb, var(--color-text) 10%, transparent)',
                            }}
                        >
                            <div className="absolute inset-0 flex items-center justify-center text-sm opacity-50">
                                No images
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Page name overlay */}
                <figcaption 
                    className={cx(
                        'tile-description absolute inset-0 z-10 transition-opacity duration-200 overflow-hidden pointer-events-none flex items-center justify-center',
                        isHovered ? 'opacity-100' : 'opacity-0 group-focus:opacity-100'
                    )}
                >
                    <span 
                        className="absolute inset-0" 
                        style={{ backgroundColor: 'var(--color-background)', opacity: 0.7 }}
                    />
                    <span className="grid-caption text-center relative z-10 px-4">
                        {displayName}
                    </span>
                </figcaption>
            </Link>
            {arrange && (
                <div className="pointer-events-none absolute inset-0 z-30">
                    <div className="absolute left-0 top-0 z-10 flex items-start text-xs">
                        <span className="block bg-white/80 px-2 py-1.5 font-medium text-gray-900">
                            {tabIndex || 1}
                        </span>
                    </div>
                    <div className="pointer-events-auto">
                        <ImageTileActionButtons
                            hasCrop={Boolean(crops[imageIds[0]])}
                            editTitle="Edit link"
                            onEdit={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({ action: 'ui-edit-link', kind: 'link', id: pageId });
                            }}
                            onCrop={imageIds[0] ? (event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({
                                    action: 'ui-crop',
                                    kind: 'link',
                                    id: imageIds[0],
                                    pageId,
                                });
                            } : undefined}
                            onRemove={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();
                                onTileEdit?.({ action: 'remove', kind: 'link', id: pageId });
                            }}
                            removeTitle="Remove link"
                            hideNarrow={false}
                        />
                    </div>
                </div>
            )}
        </figure>
    );
}

DirectoryTile.propTypes = {
    link: PropTypes.shape({
        pageId: PropTypes.string.isRequired,
        description: PropTypes.string,
        imageIds: PropTypes.arrayOf(PropTypes.string),
        cropRatio: PropTypes.string,
        cycleDuration: PropTypes.number,
        crops: PropTypes.object,
        linkedPage: PropTypes.object,
        firstImage: PropTypes.object,
    }).isRequired,
    files: PropTypes.object.isRequired,
    tabIndex: PropTypes.number,
    arrange: PropTypes.bool,
    over: PropTypes.bool,
    onDragStart: PropTypes.func,
    onDragOver: PropTypes.func,
    onDrop: PropTypes.func,
    onDragEnd: PropTypes.func,
    onTileEdit: PropTypes.func,
};

export default DirectoryMasonry;
